import { KeyRound, Trash2, ShieldCheck, User as UserIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDate } from "@/lib/timesheet";

const DeleteUserButton = ({ u, onDelete }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600" title="Delete">
        <Trash2 className="h-4 w-4" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
        <AlertDialogDescription>This removes their login and all of their timesheets. This cannot be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid={`confirm-delete-${u.id}`} onClick={() => onDelete(u)}>Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const UserTableRow = ({ u, isSelf, onRole, onToggleActive, onReset, onDelete, onRate }) => (
  <TableRow data-testid={`user-row-${u.id}`}>
    <TableCell>
      <div className="flex items-center gap-2 font-medium">
        {u.role !== "employee" ? <ShieldCheck className="h-4 w-4 text-[hsl(var(--accent))]" /> : <UserIcon className="h-4 w-4 text-muted-foreground" />}
        {u.name}{isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
      </div>
      <div className="text-xs text-muted-foreground">{u.position}</div>
    </TableCell>
    <TableCell className="font-mono text-sm">{u.email}</TableCell>
    <TableCell>
      <Select value={u.role} onValueChange={(v) => onRole(u, v)} disabled={isSelf}>
        <SelectTrigger data-testid={`role-select-${u.id}`} className="h-8 w-[150px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="employee">Contractor</SelectItem>
          <SelectItem value="manager">Approver</SelectItem>
          <SelectItem value="admin">Admin</SelectItem>
        </SelectContent>
      </Select>
    </TableCell>
    <TableCell>
      <Switch data-testid={`active-toggle-${u.id}`} checked={u.active} onCheckedChange={() => onToggleActive(u)} disabled={isSelf} />
    </TableCell>
    <TableCell>
      {u.role === "employee" ? (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">£
          <Input data-testid={`rate-input-${u.id}`} type="number" min="0" step="0.01" defaultValue={u.hourly_rate ?? ""} placeholder="-"
            onBlur={(e) => onRate(u, e.target.value)} className="h-8 w-24 font-mono text-foreground" aria-label="Hourly rate" />
        </div>
      ) : <span className="text-sm text-muted-foreground">-</span>}
    </TableCell>
    <TableCell className="text-sm text-muted-foreground">{formatDate(u.created_at)}</TableCell>
    <TableCell className="text-right">
      <div className="flex justify-end gap-1">
        <Button data-testid={`reset-pw-${u.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Reset password" onClick={() => onReset(u)}>
          <KeyRound className="h-4 w-4" />
        </Button>
        {!isSelf && <DeleteUserButton u={u} onDelete={onDelete} />}
      </div>
    </TableCell>
  </TableRow>
);
